/**
 * Het transport naar de eigen backend: één `request`, foutafhandeling, time-out
 * en annuleren. Weet niets van de demo en niets van losse eindpunten — die staan
 * in core/api/endpoints.ts, getypeerd met `endpoint<Verzoek, Antwoord>()`.
 */
import type { RequestOptions } from '../types/backend'

/**
 * Adres van de eigen backend, bijv. 'https://beautybewust.nl/api/v1' (één domein, LARAVEL.md B1).
 * Leeg = de site draait op de demo-backend (map demo/). Dit is de enige schakelaar.
 * Webshop en beheer gebruiken dezelfde API_BASE; klant en beheer hebben elk een eigen guard
 * op dezelfde sessiecookie (LARAVEL.md B8).
 */
export const API_BASE: string | null = apiBaseFrom(readEnv('VITE_API_BASE'))

/**
 * Vangnet voor staging en productie: `VITE_REQUIRE_BACKEND=true` in `.env.staging` en
 * `.env.production`. Is de backend dan niet ingesteld, dan start de site niet, in plaats van
 * stil op de demo te draaien en bestellingen in de browser te bewaren. In de preview en
 * lokaal staat dit niet aan, dus daar blijft de demo werken.
 */
if (readEnv('VITE_REQUIRE_BACKEND') === 'true' && !API_BASE) {
  throw new Error('VITE_REQUIRE_BACKEND staat aan, maar VITE_API_BASE is leeg. De site draait niet op de demo.')
}

/**
 * Omgevingsvariabelen via Vite (`.env.production`, zie `.env.example`). Defensief gelezen:
 * in een omgeving zonder `import.meta.env` valt de site terug op de demo.
 */
/** De Vite-omgeving (`import.meta.env`), of een leeg object buiten Vite (tests, server). Eén plek voor de cast. */
export function viteEnv(): Record<string, string | undefined> {
  return (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}
}

function readEnv(key: string): string | undefined {
  return viteEnv()[key]
}

/**
 * Leeg → demo. Gezet → moet een geldige https-URL zijn (http alleen voor localhost).
 * Een tikfout faalt hard bij het opstarten: liever een kapotte build dan een site die
 * stil op de demo draait en "bestellingen" in de browser opslaat.
 */
function apiBaseFrom(raw: string | undefined): string | null {
  const value = raw?.trim().replace(/\/+$/, '')
  if (!value) return null
  let url: URL
  try {
    url = new URL(value)
  } catch {
    throw new Error(`VITE_API_BASE is geen geldige URL: "${value}"`)
  }
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
  if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) throw new Error('VITE_API_BASE moet https gebruiken')
  return value
}

/**
 * CSRF volgens Laravel Sanctum (SPA-auth met cookies): de server zet een leesbare cookie
 * `XSRF-TOKEN`, en elke schrijfactie stuurt die terug als `X-XSRF-TOKEN`. Is de cookie er nog
 * niet (eerste bezoek), dan halen we hem één keer op via `/sanctum/csrf-cookie`.
 * Site en API staan op hetzelfde domein (`beautybewust.nl` en `beautybewust.nl/api/v1`,
 * LARAVEL.md B1), dus geen CORS en geen cookie-domein. De demo heeft dit niet nodig.
 */
const xsrfToken = () => {
  const raw = document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))?.slice('XSRF-TOKEN='.length)
  return raw ? decodeURIComponent(raw) : undefined
}

let csrfCookie: Promise<void> | null = null
/** `force`: ook ophalen als er al een cookie is — na een 419 is die verlopen. */
const ensureCsrfCookie = async (force = false) => {
  if ((!force && xsrfToken()) || API_BASE === null) return
  csrfCookie ??= fetch(`${new URL(API_BASE).origin}/sanctum/csrf-cookie`, { credentials: 'include' })
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => {
      csrfCookie = null
    })
  await csrfCookie
}

/** Of de site op een echte backend draait. */
export const hasBackend = API_BASE !== null

export type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

const DEFAULT_TIMEOUT_MS = 10_000

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    /** Foutmeldingen per veld, als de backend die meestuurt. */
    public fields?: Record<string, string>,
    /** Machineleesbare foutcode van de backend, bijv. 'total_mismatch'. */
    public code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Door de aanroeper geannuleerd (wegnavigeren, nieuwere aanvraag). Nooit aan de gebruiker tonen. */
export const isAbortError = (err: unknown): boolean => err instanceof DOMException && err.name === 'AbortError'

/** Uniek id per aanvraag, zodat de backend dubbele verzendingen kan negeren. */
export const newRequestId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

// ——— Getypeerde eindpunten ———

/** Alleen op typeniveau: koppelt verzoek- en antwoordtype aan een eindpunt. Bestaat niet tijdens runtime. */
declare const contract: unique symbol

export type Endpoint<Req, Res, A extends unknown[]> = {
  method: Method
  path: (...args: A) => string
  /** Optionele runtime-controle van het antwoord. Gooit bij een ongeldig antwoord. */
  parse?: (raw: unknown) => Res
  /** 'text': het antwoord is platte tekst (bijv. een CSV-export), geen JSON. */
  format?: 'text'
  readonly [contract]?: { req: Req; res: Res }
}

/**
 * Een eindpunt definiëren: `endpoint<OrderRequest, OrderResponse>()({ method, path })`.
 * `Req = void` betekent: zonder body. De compiler controleert daarna bij elke `call`
 * zowel de body als het antwoordtype — een verkeerd gekoppeld eindpunt compileert niet.
 */
export const endpoint =
  <Req = void, Res = void>() =>
  <A extends unknown[] = []>(def: {
    method: Method
    path: (...args: A) => string
    parse?: (raw: unknown) => Res
    format?: 'text'
  }): Endpoint<Req, Res, A> =>
    def

/** Zonder body mag je hem weglaten; met body is hij verplicht en getypeerd. */
type BodyArgs<Req> = [Req] extends [void] ? [body?: undefined, opts?: RequestOptions] : [body: Req, opts?: RequestOptions]

/** Een eindpunt uit core/api/endpoints.ts aanroepen. Het antwoordtype komt uit het eindpunt, niet van de aanroeper. */
export function call<Req, Res, A extends unknown[]>(
  ep: Endpoint<Req, Res, A>,
  args: A,
  ...[body, opts]: BodyArgs<Req>
): Promise<Res> {
  return request(ep.method, ep.path(...args), body, opts, ep.parse, ep.format)
}

/** Een bepaalde foutstatus betekent "niets" (bijv. 401 = niet ingelogd, 404 = bestaat niet). */
export async function nullOn<T>(status: number, promise: Promise<T>): Promise<T | null> {
  try {
    return await promise
  } catch (err) {
    if (err instanceof ApiError && err.status === status) return null
    throw err
  }
}

// ——— Transport ———

async function request<Res>(
  method: Method,
  path: string,
  body: unknown,
  opts: RequestOptions | undefined,
  parse: ((raw: unknown) => Res) | undefined,
  format: 'text' | undefined,
  /** Al één keer herhaald na een 419: nooit een tweede keer, dus geen lus. */
  retried = false,
): Promise<Res> {
  if (!API_BASE) throw new ApiError('Geen backend ingesteld')
  const external = opts?.signal
  if (external?.aborted) throw abortError()

  // Eén controller voor time-out én annuleren door de aanroeper. De listener wordt altijd
  // weer losgemaakt, zodat een lang levende signal (bijv. van een scherm) niet volloopt.
  const controller = new AbortController()
  let timedOut = false
  const timer = globalThis.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS)
  const forward = () => controller.abort()
  external?.addEventListener('abort', forward, { once: true })

  try {
    let res: Response
    try {
      if (method !== 'GET') await ensureCsrfCookie()
      const token = method !== 'GET' ? xsrfToken() : undefined
      // Een body met een bestand gaat als multipart; de browser zet dan zelf de boundary.
      const multipart = hasFile(body)
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
          Accept: format === 'text' ? 'text/csv, text/plain, application/json' : 'application/json',
          // Laravel geeft hiermee JSON-fouten (401/419/422) in plaats van een redirect naar een loginpagina.
          'X-Requested-With': 'XMLHttpRequest',
          ...(body !== undefined && !multipart ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { 'X-XSRF-TOKEN': token } : {}),
        },
        body: body === undefined ? undefined : multipart ? toFormData(body) : JSON.stringify(body),
        // Sessies lopen via een httpOnly-cookie van de backend; nooit een token in de browseropslag.
        credentials: 'include',
        signal: controller.signal,
      })
    } catch {
      throw networkError(timedOut, external)
    }

    // 419 = het CSRF-token is verlopen (tab lang open, sessie vernieuwd). Eén keer een verse
    // cookie halen en hetzelfde verzoek herhalen. Veilig voor schrijfacties: Laravel weigert
    // een 419 vóórdat er iets verwerkt is, en de requestId maakt een herhaling idempotent.
    if (res.status === 419 && method !== 'GET' && !retried) {
      await ensureCsrfCookie(true)
      return await request(method, path, body, opts, parse, format, true)
    }

    if (!res.ok) {
      const data = await readJson(res, controller, timedOut, external).catch(() => null)
      const err = isRecord(data) ? data : {}
      throw new ApiError(
        typeof err.message === 'string' ? err.message : `De server gaf een fout (${res.status})`,
        res.status,
        isStringRecord(err.fields) ? err.fields : undefined,
        typeof err.code === 'string' ? err.code : undefined,
      )
    }
    if (res.status === 204) return undefined as Res
    if (format === 'text') {
      return (await res.text().catch(() => {
        throw networkError(timedOut || controller.signal.aborted, external)
      })) as Res
    }

    const raw = await readJson(res, controller, timedOut, external)
    if (!parse) return raw as Res
    try {
      return parse(raw)
    } catch (err) {
      throw new ApiError(`Onverwacht antwoord van de server${err instanceof Error ? `: ${err.message}` : ''}`, res.status)
    }
  } finally {
    globalThis.clearTimeout(timer)
    external?.removeEventListener('abort', forward)
  }
}

/** Bevat de body (op het eerste niveau) een bestand? Dan multipart in plaats van JSON. */
function hasFile(body: unknown): body is Record<string, unknown> {
  return isRecord(body) && Object.values(body).some((v) => typeof Blob !== 'undefined' && v instanceof Blob)
}

/**
 * Platte body → FormData. Bestanden als `photo.jpg`, getallen en booleans als tekst,
 * `undefined` weggelaten. Geneste objecten worden JSON (Laravel: `json_decode` in de request).
 */
function toFormData(body: Record<string, unknown>): FormData {
  const form = new FormData()
  for (const [key, value] of Object.entries(body)) {
    if (value === undefined || value === null) continue
    if (value instanceof Blob) form.append(key, value, value instanceof File ? value.name : `${key}.jpg`)
    else if (typeof value === 'object') form.append(key, JSON.stringify(value))
    else form.append(key, String(value))
  }
  return form
}

/** De body lezen kan óók afbreken (time-out) of mislukken (HTML van een proxy i.p.v. JSON). */
async function readJson(res: Response, controller: AbortController, timedOut: boolean, external?: AbortSignal) {
  const text = await res.text().catch(() => {
    throw networkError(timedOut || controller.signal.aborted, external)
  })
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    // Voorheen werd dit "Geen verbinding" — misleidend: de server antwoordde wél.
    throw new ApiError('De server gaf een onleesbaar antwoord', res.status)
  }
}

function networkError(timedOut: boolean, external?: AbortSignal): Error {
  if (external?.aborted) return abortError()
  return new ApiError(timedOut ? 'De server reageert niet op tijd' : 'Geen verbinding met de server')
}

const abortError = () => new DOMException('Geannuleerd', 'AbortError')

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isStringRecord = (v: unknown): v is Record<string, string> =>
  isRecord(v) && Object.values(v).every((x) => typeof x === 'string')
